// file: /src/Pages/NewReservation/StepOne/algorithm/isDateAvailable.js

const moment = require('moment-timezone');
const { getAvailableTimeblocks } = require('./getAvailableTimeblocks');
const { daysOfWeekEnglish } = require('./restaurant_data/openinghours');
const { filterTimeblocksByStopTimes } = require('./filters/timeFilter');
const { filterTimeblocksByMaxGroups } = require('./filters/maxGroupsFilter');

const TIMEZONE = 'Europe/Brussels';

/**
 * Helper to see if a day-of-week has the selected giftcard for any meal 
 * (breakfast, lunch, or dinner).
 * 
 * @param {Object} data - The main restaurant data object.
 * @param {string} dateStr - The date string in "YYYY-MM-DD" format.
 * @param {string} giftcard - The selected giftcard name (non-empty).
 * @returns {boolean} True if that day-of-week has the giftcard in any meal.
 */
function dayHasGiftcard(data, dateStr, giftcard) {
  if (!giftcard || !giftcard.trim()) return true; // No giftcard => no restriction

  const m = moment.tz(dateStr, 'YYYY-MM-DD', TIMEZONE);
  if (!m.isValid()) return false;

  const englishDay = daysOfWeekEnglish[m.day()]; // e.g. "Monday"

  // Check breakfast, lunch, dinner
  const mealTypes = ['breakfast', 'lunch', 'dinner'];
  for (let meal of mealTypes) {
    const ohKey = `openinghours-${meal}`;
    if (
      data[ohKey] &&
      data[ohKey].schemeSettings &&
      data[ohKey].schemeSettings[englishDay]
    ) {
      const daySettings = data[ohKey].schemeSettings[englishDay];

      // If a meal has no giftcard restrictions, it allows all giftcards
      if (daySettings.giftcardsEnabled !== true) {
        continue;
      }

      // If the array is empty, that means no giftcards are accepted for this meal
      if (!Array.isArray(daySettings.giftcards) || daySettings.giftcards.length === 0) {
        continue;
      }

      // Check if the selected giftcard is in the allowed list
      if (daySettings.giftcards.includes(giftcard)) {
        return true;
      }
    }
  }

  // If we've gone through all meals and none allows the giftcard, return false
  return false;
}

/**
 * Checks if a date is within the allowed future range defined by dagenInToekomst.
 * @param {Object} data - The main data object (to access general settings).
 * @param {string} dateStr - The date string (YYYY-MM-DD).
 * @returns {boolean} true if within range, false otherwise.
 */
function isDateWithinAllowedRange(data, dateStr) {
  let dagenInToekomst = 90;
  if (
    data['general-settings'] &&
    data['general-settings'].dagenInToekomst &&
    parseInt(data['general-settings'].dagenInToekomst, 10) > 0
  ) {
    dagenInToekomst = parseInt(data['general-settings'].dagenInToekomst, 10);
  }

  // Now in Brussels
  const nowBxl = moment.tz(TIMEZONE);
  const maxAllowedEndOfDay = nowBxl.clone().add(dagenInToekomst, 'days').endOf('day');

  // Target date anchored to Brussels
  const targetStartOfDay = moment.tz(dateStr, 'YYYY-MM-DD', TIMEZONE).startOf('day');

  return targetStartOfDay.isSameOrBefore(maxAllowedEndOfDay);
}

/**
 * Checks if a date is available for a reservation of a specified number of guests,
 * and also (if a giftcard is selected) whether that date's day-of-week supports that giftcard.
 *
 * @param {Object} data - The main data object containing settings and meal information.
 * @param {string} dateStr - The date string in "YYYY-MM-DD" format.
 * @param {Array} reservations - An array of reservation objects.
 * @param {number} guests - The number of guests for the reservation.
 * @param {string|null} selectedGiftcard - The selected giftcard (if any).
 * @returns {boolean} - Returns true if the date meets the giftcard requirement (if any)
 *                      and has at least one available timeblock, false otherwise.
 */
function isDateAvailable(data, dateStr, reservations, guests, selectedGiftcard) {
  // If a giftcard is specified, ensure the day-of-week supports it
  if (selectedGiftcard && !dayHasGiftcard(data, dateStr, selectedGiftcard)) {
    return false;
  }

  // Check if date is within allowed range
  if (!isDateWithinAllowedRange(data, dateStr)) {
    return false;
  }

  // Get available timeblocks using the existing logic
  const availableTimeblocks = getAvailableTimeblocks(
    data, 
    dateStr, 
    reservations, 
    guests, 
    selectedGiftcard
  );

  // If no timeblocks available after initial filtering, return false immediately
  if (Object.keys(availableTimeblocks).length === 0) {
    return false;
  }

  // Apply max groups filter
  const timeBlocksAfterMaxGroups = filterTimeblocksByMaxGroups(
    data,
    dateStr,
    availableTimeblocks,
    reservations,
    guests
  );

  // If no timeblocks available after max groups filtering, return false
  if (Object.keys(timeBlocksAfterMaxGroups).length === 0) {
    return false;
  }

  // Apply stop time filter as a final step
  const filteredTimeblocks = filterTimeblocksByStopTimes(
    timeBlocksAfterMaxGroups, 
    data, 
    dateStr
  );

  // Return true only if we have at least one available timeblock after all filtering
  return Object.keys(filteredTimeblocks).length > 0;
}

module.exports = {
  isDateAvailable,
};
